/**
 * `tWithFallback` — render template-driven content with a graceful fallback.
 *
 * Template/screen/field text comes from two sources:
 *   1. The local UDS record (always present — the source of truth at edit
 *      time).
 *   2. A UILM-published translation, keyed by `uilm*Key` on the record.
 *
 * Before a template has been seeded — or while UILM is still publishing
 * — the key resolves to itself, so `t('NDA_TITLE')` would render the
 * literal string `NDA_TITLE` in the UI. Bad UX.
 *
 * This helper does the conditional:
 *   - If the key is empty/null/undefined → return the local fallback.
 *   - Otherwise call `t(key, { defaultValue: fallback })`, which is
 *     i18next's native pattern: returns the published value when one
 *     exists, the fallback when the key isn't loaded yet.
 *
 * Use it everywhere that renders editable template content.
 *
 *   t(field.uilmLabelKey ?? '', { defaultValue: field.label })
 *   ↓
 *   tWithFallback(t, field.uilmLabelKey, field.label)
 */

// Loose `t` signature — avoids dragging i18next's TFunction generics
// through every consumer.
type TFn = (key: string, options?: { defaultValue?: string }) => string;

/**
 * UILM returns this literal string as the *value* for keys that exist in
 * its catalog but have no published translation for the requested
 * language. i18next sees the key as present and skips `defaultValue`, so
 * the placeholder would otherwise leak into the UI. Detect it and fall
 * through to the caller's local fallback.
 *
 * Whitespace inside the brackets is tolerated — the platform has been
 * inconsistent about spacing.
 */
const UILM_MISSING_VALUE = /^\[\s*KEY\s+MISSING\s*\]$/i;

export function tWithFallback(t: TFn, key: string | null | undefined, fallback: string): string {
  if (!key) return fallback;
  const translated = t(key, { defaultValue: fallback });
  if (UILM_MISSING_VALUE.test(translated)) return fallback;
  return translated;
}
